import type { RefObject } from 'react';
import { useContext, useEffect, useRef } from 'react';

import { TabContext } from '#/containers/TabContext';
import { usePrevious } from '#/hooks/usePrevious';

const STEP_FOCUS_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]';

function shouldSkipTabbing(element: HTMLElement) {
  const noTabAttr = element.getAttribute('data-no-tab');
  const skipTabAttr = element.getAttribute('data-skip-tab');

  return noTabAttr === 'true' || skipTabAttr === 'true';
}

export function useStepFocusManagement({
  currentStep,
}: {
  currentStep: number;
}): RefObject<HTMLDivElement | null> {
  const stepRef = useRef<HTMLDivElement | null>(null);
  const isTabbing = useContext(TabContext) || false;
  const prevState = usePrevious({
    isTabbing,
    currentStep,
  });

  useEffect(() => {
    if (!stepRef.current) return;

    const inputs = Array.from(
      stepRef.current.querySelectorAll<HTMLElement>(STEP_FOCUS_SELECTOR),
    ).filter((input) => !shouldSkipTabbing(input));

    inputs.forEach((element) => {
      const input = element;

      input.tabIndex = 0;
    });

    if (isTabbing && prevState && currentStep !== prevState.currentStep) {
      inputs[0]?.focus();
    }
  }, [currentStep, isTabbing, prevState]);

  return stepRef;
}
